import { useState } from 'react';
import { motion } from 'framer-motion';
import { Check, X, Sparkles } from 'lucide-react';
import { Link } from 'react-router-dom';
import StaticPageLayout from '../../components/StaticPageLayout';
import { useThemeStore } from '../../store/theme';

const plans = [
  {
    name: 'Free',
    monthlyPrice: 0,
    yearlyPrice: 0,
    description: 'Get started with AI employees',
    credits: 100,
    features: [
      { text: 'All AI Employees', included: true },
      { text: '100 credits/month', included: true },
      { text: 'Basic integrations', included: true },
      { text: 'Community support', included: true },
      { text: 'Brain AI training', included: false },
      { text: 'Priority support', included: false },
      { text: 'Custom employees', included: false },
      { text: 'API access', included: false },
    ],
    cta: 'Start Free',
  },
  {
    name: 'Pro',
    monthlyPrice: 25,
    yearlyPrice: 250,
    description: 'Perfect for small teams',
    credits: 1500,
    features: [
      { text: 'All AI Employees', included: true },
      { text: '1,500 credits/month', included: true },
      { text: 'All integrations', included: true },
      { text: 'Email support', included: true },
      { text: 'Brain AI training', included: true },
      { text: 'Priority support', included: false },
      { text: 'Custom employees', included: false },
      { text: 'API access', included: false },
    ],
    cta: 'Get Started',
  },
  {
    name: 'Professional',
    monthlyPrice: 50,
    yearlyPrice: 500,
    description: 'For growing businesses',
    credits: 3000,
    popular: true,
    features: [
      { text: 'All AI Employees', included: true },
      { text: '3,000 credits/month', included: true },
      { text: 'All integrations', included: true },
      { text: 'Priority support', included: true },
      { text: 'Advanced Brain AI', included: true },
      { text: 'Team collaboration', included: true },
      { text: 'Analytics dashboard', included: true },
      { text: 'API access', included: false },
    ],
    cta: 'Get Started',
  },
  {
    name: 'Enterprise',
    monthlyPrice: 150,
    yearlyPrice: 1500,
    description: 'Enterprise-grade features',
    credits: 'Unlimited',
    features: [
      { text: 'All AI Employees', included: true },
      { text: 'Unlimited credits', included: true },
      { text: 'All integrations', included: true },
      { text: 'Dedicated support', included: true },
      { text: 'Custom Brain training', included: true },
      { text: 'White-label option', included: true },
      { text: 'Custom employees', included: true },
      { text: 'Full API access', included: true },
    ],
    cta: 'Contact Sales',
  },
];

const faqs = [
  {
    question: 'What are credits?',
    answer:
      'Credits are the currency used to interact with AI employees. Each message or task consumes credits based on complexity. Simple messages use 1 credit, while complex tasks may use more.',
  },
  {
    question: 'Can I change plans anytime?',
    answer:
      'Yes! You can upgrade or downgrade your plan at any time. When upgrading, you\'ll be charged the prorated difference. When downgrading, the change takes effect at your next billing cycle.',
  },
  {
    question: 'What happens if I run out of credits?',
    answer:
      'You can purchase additional credits at any time, or upgrade to a higher plan. We\'ll notify you when you\'re running low so you\'re never caught off guard.',
  },
  {
    question: 'Is there a free trial?',
    answer:
      'Our Free plan is essentially a permanent free trial with 100 credits per month. You can use it indefinitely to test our AI employees before committing to a paid plan.',
  },
  {
    question: 'Do you offer refunds?',
    answer:
      'We offer a 14-day money-back guarantee on all paid plans. If you\'re not satisfied, contact support within 14 days of your purchase for a full refund.',
  },
  {
    question: 'What payment methods do you accept?',
    answer:
      'We accept all major credit cards (Visa, Mastercard, American Express), PayPal, and bank transfers for Enterprise plans.',
  },
];

export default function Pricing() {
  const [isAnnual, setIsAnnual] = useState(false);
  const { theme } = useThemeStore();
  const isDark = theme === 'dark';

  return (
    <StaticPageLayout
      title="Simple, Transparent Pricing"
      subtitle="Choose the plan that fits your needs. All plans include a 14-day money-back guarantee."
      fullWidth={true}
    >
      {/* Billing Toggle */}
      <div className="flex items-center justify-center gap-4 mb-12">
        <button
          onClick={() => setIsAnnual(false)}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
            !isAnnual
              ? 'bg-purple-500 text-white'
              : (isDark ? 'text-gray-400 hover:text-white' : 'text-slate-500 hover:text-slate-900')
          }`}
        >
          Monthly
        </button>
        <button
          onClick={() => setIsAnnual(true)}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${
            isAnnual
              ? 'bg-purple-500 text-white'
              : (isDark ? 'text-gray-400 hover:text-white' : 'text-slate-500 hover:text-slate-900')
          }`}
        >
          Yearly
          <span className={`text-xs px-1.5 py-0.5 rounded ${isAnnual ? 'bg-green-500/30 text-green-200' : 'bg-green-500/20 text-green-500'}`}>
            -17%
          </span>
        </button>
      </div>

      {/* Pricing Cards */}
      <section className="mb-16 w-full">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 w-full">
          {plans.map((plan, index) => (
            <motion.div
              key={plan.name}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className={`relative p-6 rounded-2xl border flex flex-col h-full ${
                plan.popular
                  ? isDark
                    ? 'bg-gradient-to-b from-purple-500/10 to-cyan-500/10 border-purple-500/30'
                    : 'bg-gradient-to-b from-purple-50 to-cyan-50 border-purple-300 shadow-lg'
                  : isDark
                    ? 'bg-dark-800/50 border-white/5'
                    : 'bg-white border-slate-200 shadow-sm'
              }`}
            >
              {plan.popular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-gradient-to-r from-purple-500 to-cyan-500 rounded-full text-xs font-medium text-white flex items-center gap-1">
                  <Sparkles className="w-3 h-3" />
                  Most Popular
                </div>
              )}

              <h3 className={`text-xl font-bold mb-1 ${isDark ? 'text-white' : 'text-slate-900'}`}>{plan.name}</h3>
              <p className={`text-sm mb-4 ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>{plan.description}</p>

              <div className="mb-6">
                <div className="flex items-baseline gap-1">
                  <span className={`text-4xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                    ${isAnnual ? plan.yearlyPrice : plan.monthlyPrice}
                  </span>
                  <span className={isDark ? 'text-gray-500' : 'text-slate-400'}>/{isAnnual ? 'year' : 'month'}</span>
                </div>
                {isAnnual && plan.monthlyPrice > 0 && (
                  <p className={`text-sm mt-1 ${isDark ? 'text-green-400' : 'text-green-600'}`}>
                    Save ${plan.monthlyPrice * 12 - plan.yearlyPrice}/year
                  </p>
                )}
                <p className={`text-sm mt-1 ${isDark ? 'text-gray-500' : 'text-slate-400'}`}>
                  {typeof plan.credits === 'number'
                    ? `${plan.credits.toLocaleString()} credits/month`
                    : plan.credits}
                </p>
              </div>

              <ul className="space-y-3 mb-6 flex-grow">
                {plan.features.map((feature) => (
                  <li key={feature.text} className="flex items-start gap-2">
                    {feature.included ? (
                      <Check className={`w-5 h-5 shrink-0 ${isDark ? 'text-green-400' : 'text-green-600'}`} />
                    ) : (
                      <X className={`w-5 h-5 shrink-0 ${isDark ? 'text-gray-600' : 'text-slate-300'}`} />
                    )}
                    <span className={feature.included ? (isDark ? 'text-gray-300' : 'text-slate-700') : (isDark ? 'text-gray-600' : 'text-slate-400')}>
                      {feature.text}
                    </span>
                  </li>
                ))}
              </ul>

              <Link
                to={plan.name === 'Enterprise' ? '/contact' : '/login'}
                className={`block w-full py-3 rounded-xl font-medium text-center transition-all ${
                  plan.popular
                    ? 'bg-gradient-to-r from-purple-500 to-cyan-500 text-white hover:shadow-glow-sm'
                    : isDark
                      ? 'bg-dark-700 text-white hover:bg-dark-600'
                      : 'bg-slate-100 text-slate-900 hover:bg-slate-200'
                }`}
              >
                {plan.cta}
              </Link>
            </motion.div>
          ))}
        </div>
      </section>

      {/* FAQs */}
      <section className="mb-16 w-full">
        <h2 className={`text-2xl font-bold mb-8 text-center ${isDark ? 'text-white' : 'text-slate-900'}`}>
          Frequently Asked Questions
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {faqs.map((faq) => (
            <div
              key={faq.question}
              className={`p-6 rounded-2xl border ${
                isDark ? 'bg-dark-800/50 border-white/5' : 'bg-white border-slate-200 shadow-sm'
              }`}
            >
              <h3 className={`text-lg font-semibold mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>{faq.question}</h3>
              <p className={isDark ? 'text-gray-400' : 'text-slate-500'}>{faq.answer}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className={`text-center p-8 rounded-2xl border w-full ${
        isDark
          ? 'bg-gradient-to-br from-purple-500/10 to-cyan-500/10 border-white/5'
          : 'bg-gradient-to-br from-purple-50 to-cyan-50 border-purple-100'
      }`}>
        <h2 className={`text-2xl font-bold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>Still Have Questions?</h2>
        <p className={`mb-6 ${isDark ? 'text-gray-400' : 'text-slate-600'}`}>
          Our team is here to help. Contact us for a personalized demo or to discuss custom
          enterprise solutions.
        </p>
        <Link
          to="/contact"
          className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-purple-500 to-cyan-500 rounded-xl font-medium text-white shadow-lg shadow-purple-500/25 hover:shadow-purple-500/40 transition-shadow"
        >
          Contact Sales
        </Link>
      </section>
    </StaticPageLayout>
  );
}
